import { Badge } from '../ui/badge';
import { CheckCircle2, Circle } from 'lucide-react';

// Progress ring component
export const ProgressRing = ({ percent, size = 80, strokeWidth = 8, color }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-muted/30"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
          {percent}%
        </span>
      </div>
    </div>
  );
};

// Status indicator component
export const StatusIndicator = ({ status }) => {
  if (status === 'complete') {
    return <CheckCircle2 className="w-5 h-5 text-[rgb(var(--success))]" />;
  }
  if (status === 'in_progress') {
    return <Circle className="w-5 h-5 text-[rgb(var(--warning))] fill-[rgb(var(--warning))]/30" />;
  }
  return <Circle className="w-5 h-5 text-muted-foreground/30" />;
};

// Traffic light badge - Smart algorithm based on time and progress
export const TrafficLight = ({ percent, dueDate, initiatives = [] }) => {
  const now = new Date();
  const due = dueDate ? new Date(dueDate) : null;

  const hasOverdueInitiatives = initiatives.some(init => {
    if (init.status === 'complete') return false;
    if (!init.dueDate) return false;
    return new Date(init.dueDate) < now;
  });

  if (percent === 100) {
    return (
      <Badge className="bg-[rgb(var(--success))]/10 text-[rgb(var(--success))] border-[rgb(var(--success))]/20">
        Complete
      </Badge>
    );
  }

  if (due && due < now && percent < 100) {
    return (
      <Badge className="bg-[rgb(var(--destructive))]/10 text-[rgb(var(--destructive))] border-[rgb(var(--destructive))]/20">
        Behind
      </Badge>
    );
  }

  if (hasOverdueInitiatives) {
    return (
      <Badge className="bg-[rgb(var(--destructive))]/10 text-[rgb(var(--destructive))] border-[rgb(var(--destructive))]/20">
        Behind
      </Badge>
    );
  }

  const daysUntilDue = due ? Math.ceil((due - now) / (1000 * 60 * 60 * 24)) : 999;

  if (daysUntilDue <= 30 && percent < 80) {
    return (
      <Badge className="bg-[rgb(var(--warning))]/10 text-[rgb(var(--warning))] border-[rgb(var(--warning))]/20">
        At Risk
      </Badge>
    );
  }

  if (daysUntilDue <= 60 && percent < 60) {
    return (
      <Badge className="bg-[rgb(var(--warning))]/10 text-[rgb(var(--warning))] border-[rgb(var(--warning))]/20">
        At Risk
      </Badge>
    );
  }

  return (
    <Badge className="bg-[rgb(var(--success))]/10 text-[rgb(var(--success))] border-[rgb(var(--success))]/20">
      On Track
    </Badge>
  );
};

// Format date for display
export const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Check if date is overdue
export const isOverdue = (dateStr, status) => {
  if (!dateStr || status === 'complete') return false;
  return new Date(dateStr) < new Date();
};

// Generate unique ID
export const generateId = () => Math.random().toString(36).substring(2, 9);
